import React, { useMemo, useState } from "react";
import dropdownIcon from "../../assets/componentsIcons/dropdown/dropdown.svg";
import {
  DocumentService,
  LeaseAgreementVM,
  TenantService,
  TenantVM,
} from "@propertelligent/client-api";
import ActionMenu from "../actionMenus/action-menu";
import { Link } from "react-router-dom";
import apiHelper from "../../utils/apiHelper";
import { GetImages } from "../../utils/GetImages";
import { downloadBase64File } from "../../utils/downloadBase64File.helper";
import { useLeaseInfoTooltip } from "../../hooks/useLeaseInfoTooltip";
import UserIcon from "../../assets/svgs/unitIcons/userIcon";
import CurrencyTooltip from "../../components/currencytooltip/CurrencyTooltip";
import LargeTooltip from "../tooltips/large-tooltip";

interface PastLeaseCardItemProps {
  listingData: LeaseAgreementVM;
  IsFooter?: boolean;
  className?: string;
  isNotLink?: boolean;
  hasWriteAccess?: boolean;
}

const PastLeaseCardItem: React.FC<PastLeaseCardItemProps> = ({
  listingData,
  IsFooter = true,
  className = "",
  isNotLink = false,
  hasWriteAccess = true,
}) => {
  const [fetchedTenant, setFetchedTenant] = useState<TenantVM | null>(null);

  const {
    handleTenantInfoClick: openTenantTooltip,
    setTooltipState,
    tooltipState,
    handleCloseTooltip,
  } = useLeaseInfoTooltip();

  const handleDownloadAgreement = async (documentId: string | number) => {
    if (!documentId) return;
    try {
      const res = await apiHelper(
        DocumentService.getApiDocumentBase641,
        { showNotification: false },
        documentId,
      );
      downloadBase64File({
        base64Data: res?.data?.success,
        fileName: res?.data?.fileName,
        fallbackName: `Agreement_${listingData?.id}`,
      });
    } catch (err) {
      console.error("Error downloading agreement:", err);
    }
  };

  const handleViewMoreClick = async (
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    const rect = event.currentTarget.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const spaceBelow = windowHeight - rect.bottom;
    const tooltipHeight = 400;

    let placement: "top" | "bottom" = "bottom";
    let top = rect.bottom + window.scrollY + 8;

    if (spaceBelow < tooltipHeight) {
      placement = "top";
      top = rect.top + window.scrollY - 10;
    }

    const position = {
      top,
      left: rect.left + window.scrollX,
      placement,
    };

    window.dispatchEvent(new Event("closeAllLeaseTooltips"));

    setTooltipState({
      isOpen: true,
      position,
      description: (
        <div className="contact-summary-card text-center py-4">
          <div
            className="spinner-border spinner-border-sm text-primary"
            role="status"
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="ms-2">Loading tenant details...</span>
        </div>
      ),
      title: "Tenant Details",
    });

    const tenantId = (listingData as any).tenantId;

    if (!fetchedTenant && tenantId) {
      try {
        const res = await apiHelper(
          TenantService.getApiTenant1,
          { showNotification: false },
          tenantId,
        );
        if (res?.data?.success) {
          setFetchedTenant(res.data.success);
          openTenantTooltip(event, res.data.success, position);
        }
      } catch (err) {
        setTooltipState((prev) => ({
          ...prev,
          description: (
            <div className="text-danger text-center py-3">
              Failed to load tenant details
            </div>
          ),
        }));
      }
    } else if (fetchedTenant) {
      openTenantTooltip(event, fetchedTenant, position);
    }
  };

  const menuItems = useMemo(
    () => [
      {
        icon: GetImages.downloadIcon,
        label: "Download Agreement",
        action: async () => {
          if (listingData.leaseDocumentId)
            await handleDownloadAgreement(listingData.leaseDocumentId);
        },
        isDivider: false,
        isDestructive: false,
      },
    ],
    [listingData.leaseDocumentId],
  );

  // Card Content JSX (Reusable for Link and Div)
  const CardContent = (
    <>
      {/* <span className={`listing-status-badge lease-period-badge ${listingData?.leasePeriod === 2 ? "pending" : listingData?.leasePeriod === 3 ? "danger" : ""}`}>
        {listingData?.formatted?.LeasePeriod || "---"}
      </span> */}
      <div className="listing-card-header">
        <div className="listing-details">
          <div className="listing-info-container">
            <div className="listing-info">
              <h3 className="listing-startdate">
                {listingData?.formatted?.StartDateDateString || "N/A"}
              </h3>
              <p className="listing-type">Start Date</p>
            </div>
            <div className="listing-info-past">
              <h3 className="listing-enddate">
                {listingData?.formatted?.EndDateDateString || "N/A"}
              </h3>
              <p className="listing-type">End Date</p>
            </div>
            <div className="financial-metrics">
              <div className="metric">
                <CurrencyTooltip
                  content={listingData.formatted.RentRaw}
                  formatted={listingData.formatted.Rent}
                >
                  <p className="metric-value">
                    {listingData?.formatted?.Rent || "N/A"}
                  </p>
                </CurrencyTooltip>
                <p className="metric-label">Rent</p>
              </div>
              <div className="separator-vertical" />
              <div className="metric">
                <CurrencyTooltip
                  content={listingData.formatted.SecurityDepositRaw}
                  formatted={listingData.formatted.SecurityDeposit}
                >
                  <p className="metric-value">
                    {listingData?.formatted?.SecurityDeposit || "N/A"}
                  </p>
                </CurrencyTooltip>
                <p className="metric-label">Security Deposit</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div
      key={listingData.id}
      className={`listing-card past-lease-card my-2 ${className}`}
    >
      {/* Top section with isNotLink logic */}
      {isNotLink ? (
        <div
          className="listing-link"
          style={{
            textDecoration: "none",
            color: "inherit",
          }}
        >
          {CardContent}
        </div>
      ) : (
        <Link
          to={`/properties/propertydetails/${listingData.id}`}
          className="listing-link"
          style={{ textDecoration: "none", color: "inherit" }}
        >
          {CardContent}
        </Link>
      )}

      {IsFooter && (
        <>
          <div className="separator-horizontal" />
          <div className="listing-card-footer">
            <div className="listing-stats">
              <div className="stat">
                <UserIcon className="stat-icon" width="24" height="24" />
                <span className="stat-text">
                  Tenant: {listingData?.tenantName || "N/A"}
                  <div className="info-tooltip-wrapper">
                    <a
                      href="#"
                      className="listing-viewmore mx-2 ml-1"
                      onClick={handleViewMoreClick}
                    >
                      <img
                        src={GetImages.infoBlackIcon}
                        alt="info"
                        width={16}
                        height={16}
                        className="stat-icon"
                      />
                    </a>
                    {tooltipState.isOpen &&
                      tooltipState.title === "Tenant Details" && (
                        <LargeTooltip
                          isOpen={true}
                          onClose={handleCloseTooltip}
                          description={tooltipState.description}
                          position={tooltipState.position}
                          title="Tenant Details"
                          type="tenant"
                        />
                      )}
                  </div>
                </span>
              </div>
            </div>
            {listingData.leaseDocumentId && hasWriteAccess && (
              <div className="footer-actions">
                <ActionMenu
                  menuItems={menuItems}
                  trigger={
                    <button className="action-button">
                      <img
                        src={dropdownIcon}
                        alt="More"
                        className="action-icon small-action-icon"
                      />
                    </button>
                  }
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PastLeaseCardItem;
